import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert, Collapse, OverlayTrigger, Popover } from 'react-bootstrap';

const ClientHistoryModal = ({ show, onHide, cliente, historico, loading }) => {
    const [expandedPedido, setExpandedPedido] = useState(null);
    const [productImages, setProductImages] = useState({});
    const fetchingRef = useRef(new Set()); // Controle de códigos em busca
    const queueRef = useRef([]); // Fila de busca
    const processingRef = useRef(false); // Se está processando a fila

    const togglePedido = (numero) => {
        setExpandedPedido(expandedPedido === numero ? null : numero);
    };

    // Buscar imagem do produto pelo código (uma de cada vez)
    const fetchSingleImage = async (codigo) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/ecommerce-bling/produto-imagem/${encodeURIComponent(codigo)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.url) {
                    setProductImages(prev => ({ ...prev, [codigo]: data.url }));
                    return;
                }
            }
            // Se 429 (rate limit), marcar como pending pra tentar de novo depois
            if (response.status === 429) {
                fetchingRef.current.delete(codigo);
                return; // Não marca como null, pode tentar novamente
            }
            setProductImages(prev => ({ ...prev, [codigo]: null }));
        } catch (error) {
            setProductImages(prev => ({ ...prev, [codigo]: null }));
        }
    };

    // Processar fila sequencialmente com delay entre requisições
    const processQueue = useCallback(async () => {
        if (processingRef.current) return;
        processingRef.current = true;

        while (queueRef.current.length > 0) {
            const codigo = queueRef.current.shift();
            if (fetchingRef.current.has(codigo)) continue;
            fetchingRef.current.add(codigo);
            await fetchSingleImage(codigo);
            // Delay de 100ms entre cada busca (backend tem cache em memória)
            await new Promise(r => setTimeout(r, 100));
        }

        processingRef.current = false;
    }, []);

    // Enfileirar busca de imagem para um código
    const fetchProductImage = useCallback((codigo) => {
        if (productImages[codigo] !== undefined) return; // Já buscou
        if (fetchingRef.current.has(codigo)) return; // Já na fila
        queueRef.current.push(codigo);
        processQueue();
    }, [productImages, processQueue]);

    // Componente para código do produto com tooltip de imagem
    const ProductCodeWithImage = ({ item }) => {
        const imageFromItem = item.imagem_url || item.imagem || item.url_imagem;
        const cachedImage = productImages[item.codigo];
        const imageUrl = imageFromItem || cachedImage;
        const isLoading = cachedImage === undefined && !imageFromItem;

        const popover = (
            <Popover id={`popover-${item.codigo}`} style={{ maxWidth: '220px' }}>
                <Popover.Header as="h6" className="text-center py-1 bg-dark text-white" style={{ fontSize: '0.75rem' }}>
                    📦 {item.codigo}
                </Popover.Header>
                <Popover.Body className="p-2 text-center" style={{ backgroundColor: '#fff' }}>
                    {isLoading ? (
                        <div className="py-3">
                            <Spinner animation="border" size="sm" variant="secondary" />
                            <div className="small text-muted mt-1">Carregando...</div>
                        </div>
                    ) : imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={item.descricao || item.codigo}
                            style={{
                                width: '190px',
                                height: '190px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                backgroundColor: '#fff'
                            }}
                            onError={(e) => {
                                e.target.src = '/placeholder.svg';
                                e.target.style.opacity = '0.3';
                            }}
                        />
                    ) : (
                        <div className="py-3 text-muted">
                            <div style={{ fontSize: '2rem' }}>📷</div>
                            <div className="small">Imagem não disponível</div>
                        </div>
                    )}
                    <div className="text-muted mt-1" style={{
                        fontSize: '0.7rem',
                        lineHeight: '1.2',
                        maxHeight: '2.4em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                    }}>
                        {item.descricao || '-'}
                    </div>
                </Popover.Body>
            </Popover>
        );

        return (
            <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="right"
                overlay={popover}
                delay={{ show: 200, hide: 100 }}
                rootClose
                container={document.body}
                onEnter={() => {
                    if (!imageFromItem && productImages[item.codigo] === undefined) {
                        fetchProductImage(item.codigo);
                    }
                }}
            >
                <Badge
                    bg="secondary"
                    style={{ cursor: 'pointer' }}
                    className="product-code-badge"
                >
                    {item.codigo}
                </Badge>
            </OverlayTrigger>
        );
    };

    const handleModalHide = () => {
        console.log('Modal onHide triggered internally');
        if (onHide) {
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={handleModalHide} size="xl" centered scrollable enforceFocus={false}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {cliente?.contato_nome || cliente?.nome}
                    <div className="fs-6 text-muted fw-normal mt-1">
                        Histórico Completo do Cliente
                    </div>
                    <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: '0.78rem', fontWeight: 400 }}>
                        {(cliente?.cpf_cnpj) && (
                            <span className="text-muted">
                                <strong>📄</strong> {cliente.cpf_cnpj}
                            </span>
                        )}
                        {(cliente?.celular || cliente?.telefone) && (
                            <span className="text-muted">
                                <strong>📱</strong> {cliente.celular || cliente.telefone}
                            </span>
                        )}
                        {(cliente?.email) && (
                            <span className="text-muted">
                                <strong>✉️</strong> {cliente.email}
                            </span>
                        )}
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">Carregando histórico completo...</p>
                    </div>
                ) : historico ? (
                    <Container fluid className="p-0">
                        {/* Stats Cards */}
                        <Row className="mb-4 g-3">
                            <Col md={3}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="text-muted small mb-1">Total Pedidos</div>
                                        <div className="h3 mb-0 fw-bold text-primary">{historico.stats.totalPedidos}</div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="text-muted small mb-1">Total Gasto (LTV)</div>
                                        <div className="h3 mb-0 fw-bold text-success">
                                            R$ {historico.stats.totalGasto?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="text-muted small mb-1">Ticket Médio</div>
                                        <div className="h3 mb-0 fw-bold text-info">
                                            R$ {historico.stats.ticketMedio?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Body className="text-center">
                                        <div className="text-muted small mb-1">Primeira Compra</div>
                                        <div className="h5 mb-0 fw-bold">
                                            {historico.stats.primeiraCompra ? new Date(historico.stats.primeiraCompra).toLocaleDateString('pt-BR') : '-'}
                                        </div>
                                        <small className="text-muted">
                                            Última: {historico.stats.ultimaCompra ? new Date(historico.stats.ultimaCompra).toLocaleDateString('pt-BR') : '-'}
                                        </small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row>
                            {/* Top Produtos */}
                            <Col md={4} className="mb-4">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Header className="bg-white fw-bold py-3">
                                        🏆 Top Produtos Comprados
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <Table hover className="mb-0 table-sm" responsive>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-3 border-0">Produto</th>
                                                    <th className="text-end pe-3 border-0">Qtd</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {historico.topProdutos.map((prod, i) => {
                                                    const isImportado = (prod.descricao || '').toLowerCase().includes('importado');
                                                    return (
                                                        <tr key={i} className={isImportado ? 'bg-warning bg-opacity-10' : ''}>
                                                            <td className="ps-3 py-2 small">
                                                                <div className="d-flex align-items-center gap-1">
                                                                    {isImportado && (
                                                                        <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem' }}>🌍</Badge>
                                                                    )}
                                                                    <div className={`fw-bold text-truncate ${isImportado ? 'text-dark' : ''}`} style={{ maxWidth: '180px' }} title={prod.descricao}>
                                                                        {prod.descricao}
                                                                    </div>
                                                                </div>
                                                                <div className="text-muted smaller">{prod.codigo}</div>
                                                            </td>
                                                            <td className="text-end pe-3 py-2">
                                                                <Badge bg={isImportado ? "warning" : "light"} text="dark" className="border">
                                                                    {prod.qtd_total} un
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {historico.topProdutos.length === 0 && (
                                                    <tr>
                                                        <td colSpan="2" className="text-center py-3 text-muted">
                                                            Nenhum produto encontrado.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Lista de Vendas */}
                            <Col md={8} className="mb-4">
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Header className="bg-white fw-bold py-3">
                                        🛒 Histórico de Pedidos
                                        <small className="fw-normal text-muted ms-2">(Clique para ver detalhes)</small>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <div className="table-responsive">
                                            <Table hover className="mb-0 align-middle">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th className="ps-3" style={{ width: '30px' }}></th>
                                                        <th>Data</th>
                                                        <th>Número</th>
                                                        <th className="text-center">Itens</th>
                                                        <th className="text-end pe-3">Valor</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historico.vendas.map((venda, i) => (
                                                        <React.Fragment key={i}>
                                                            <tr
                                                                onClick={() => togglePedido(venda.numero)}
                                                                style={{ cursor: 'pointer' }}
                                                                className={expandedPedido === venda.numero ? 'table-active' : ''}
                                                            >
                                                                <td className="ps-3 text-center">
                                                                    <span style={{ transition: 'transform 0.2s', display: 'inline-block', transform: expandedPedido === venda.numero ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                                                        ▶
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <div className="fw-bold">
                                                                        {new Date(venda.data).toLocaleDateString('pt-BR')}
                                                                    </div>
                                                                    <small className="text-muted">
                                                                        {venda.agrupado ? 'Consolidado' : (venda.loja_nome || 'E-commerce')}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    {venda.agrupado ? (
                                                                        <span>
                                                                            #{venda.numero.split(' ')[0]}
                                                                            {venda.pedidos_agrupados > 1 && (
                                                                                <Badge bg="info" className="ms-1" style={{ fontSize: '0.65rem' }}>
                                                                                    +{venda.pedidos_agrupados - 1}
                                                                                </Badge>
                                                                            )}
                                                                        </span>
                                                                    ) : (
                                                                        <>#{venda.numero}</>
                                                                    )}
                                                                </td>
                                                                <td className="text-center">
                                                                    <Badge bg={venda.agrupado ? "info" : "light"} text={venda.agrupado ? "white" : "dark"} className="border px-2 py-1">
                                                                        {venda.qtd_itens || venda.itens?.length || 0} itens
                                                                    </Badge>
                                                                </td>
                                                                <td className="text-end pe-3 fw-bold">
                                                                    R$ {venda.valor_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                </td>
                                                            </tr>
                                                            {/* Detalhes expandidos do pedido */}
                                                            <tr>
                                                                <td colSpan="5" className="p-0 border-0">
                                                                    <Collapse in={expandedPedido === venda.numero}>
                                                                        <div>
                                                                            <div className="bg-light p-3 border-bottom">
                                                                                <h6 className="mb-2 text-muted">
                                                                                    📦 Produtos do Pedido #{venda.numero}
                                                                                </h6>
                                                                                {venda.itens && venda.itens.length > 0 ? (
                                                                                    <Table size="sm" className="mb-0 bg-white" bordered>
                                                                                        <thead className="table-secondary">
                                                                                            <tr>
                                                                                                <th>Código</th>
                                                                                                <th>Descrição</th>
                                                                                                <th className="text-center">Qtd</th>
                                                                                                <th className="text-end">Valor Unit.</th>
                                                                                                <th className="text-end">Total</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {venda.itens.map((item, j) => {
                                                                                                const isImportado = (item.descricao || '').toLowerCase().includes('importado');
                                                                                                return (
                                                                                                    <tr key={j} className={isImportado ? 'table-warning' : ''}>
                                                                                                        <td className="small">
                                                                                                            <ProductCodeWithImage item={item} />
                                                                                                        </td>
                                                                                                        <td className="small" style={{ maxWidth: '200px' }}>
                                                                                                            <div className="d-flex align-items-center gap-1">
                                                                                                                {isImportado && (
                                                                                                                    <Badge bg="warning" text="dark" style={{ fontSize: '0.55rem', padding: '2px 4px' }}>
                                                                                                                        🌍 IMP
                                                                                                                    </Badge>
                                                                                                                )}
                                                                                                                <span className={`text-truncate ${isImportado ? 'fw-semibold' : ''}`} title={item.descricao}>
                                                                                                                    {item.descricao || '-'}
                                                                                                                </span>
                                                                                                            </div>
                                                                                                        </td>
                                                                                                        <td className="text-center">
                                                                                                            <strong>{item.quantidade}</strong>
                                                                                                        </td>
                                                                                                        <td className="text-end small">
                                                                                                            R$ {(item.valor_unitario || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                                                        </td>
                                                                                                        <td className="text-end small fw-bold">
                                                                                                            R$ {((item.quantidade || 1) * (item.valor_unitario || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                                                        </td>
                                                                                                    </tr>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                        <tfoot className="table-light">
                                                                                            {/* Subtotal de produtos */}
                                                                                            <tr>
                                                                                                <td colSpan="4" className="text-end">
                                                                                                    <strong>Subtotal:</strong>
                                                                                                </td>
                                                                                                <td className="text-end fw-bold">
                                                                                                    R$ {(venda.itens.reduce((acc, item) => acc + ((item.quantidade || 1) * (item.valor_unitario || 0)), 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                                                </td>
                                                                                            </tr>
                                                                                            {/* Frete - só exibe se > 0 */}
                                                                                            {(venda.valor_frete > 0 || venda.frete > 0) && (
                                                                                                <tr className="text-info">
                                                                                                    <td colSpan="4" className="text-end">
                                                                                                        <strong>🚚 Frete:</strong>
                                                                                                    </td>
                                                                                                    <td className="text-end fw-bold">
                                                                                                        R$ {(venda.valor_frete || venda.frete || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            )}
                                                                                            {/* Total do pedido */}
                                                                                            <tr className="table-success">
                                                                                                <td colSpan="4" className="text-end">
                                                                                                    <strong>Total do Pedido:</strong>
                                                                                                </td>
                                                                                                <td className="text-end fw-bold">
                                                                                                    R$ {(venda.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                                                </td>
                                                                                            </tr>
                                                                                        </tfoot>
                                                                                    </Table>
                                                                                ) : (
                                                                                    <div className="text-muted small bg-white p-2 rounded">
                                                                                        Detalhes dos itens não disponíveis para este pedido.
                                                                                        {(venda.valor_frete > 0 || venda.frete > 0) && (
                                                                                            <div className="mt-2">
                                                                                                <strong>🚚 Frete:</strong> R$ {(venda.valor_frete || venda.frete || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </Collapse>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))}
                                                    {historico.vendas.length === 0 && (
                                                        <tr>
                                                            <td colSpan="5" className="text-center py-3 text-muted">
                                                                Nenhum pedido encontrado.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                ) : (
                    <Alert variant="warning">
                        Não foi possível carregar o histórico deste cliente.
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleModalHide}>
                    Fechar
                </Button>
                {cliente && (
                    <Button
                        variant="primary"
                        href={`https://wa.me/55${(cliente.celular || cliente.telefone || '').replace(/\D/g, '')}?text=Olá ${cliente.contato_nome?.split(' ')[0] || cliente.nome?.split(' ')[0]}, vi que você já comprou conosco antes e temos novidades!`}
                        target="_blank"
                        disabled={!cliente.celular && !cliente.telefone}
                    >
                        Chamar no WhatsApp
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ClientHistoryModal;
